/**
 * Matcher for the composer's trigger menus — '@' mentions (agents in private
 * notes, WhatsApp group members in replies), '/' canned responses, '{{'
 * variables and ':' emoji. Drop-in replacement for triggerCharacters in
 * @chatwoot/prosemirror-schema/src/mentions/plugin.
 *
 * Upstream scans from the start of the block to the END of the block, so the
 * greedy `@[^\s@]*` match runs PAST the cursor and swallows whatever follows
 * it. Typing '@' in front of existing text ("ping |cy about this") produced the
 * search term "cyabout" instead of "cy": the picker filtered down to zero rows
 * and vanished, which reads as "@ doesn't work in the middle of the chat"
 * (reported 2026-07-28). The match range leaked the same way, so accepting a
 * suggestion would have replaced the trailing word along with the trigger.
 *
 * Only text BEFORE the cursor can be part of what the agent is typing, so the
 * scan stops at the cursor. Everything else is upstream's logic verbatim.
 */
export const triggerCharacters =
  (char, minChars = 0) =>
  $position => {
    // Occurrences of 'char' followed by at least 'minChars' non-space chars,
    // starting at the beginning of the text or after a space.
    const regexp = new RegExp(`(?:^)?${char}[^\\s${char}]{${minChars},}`, 'g');

    const textFrom = $position.before();
    // Cursor, NOT $position.end() — see above.
    const textTo = $position.pos;

    const text = $position.doc.textBetween(textFrom, textTo, '\0', '\0');

    let match;

    // eslint-disable-next-line
    while ((match = regexp.exec(text))) {
      // The trigger only counts at the start of a word.
      const prefix = match.input.slice(
        Math.max(0, match.index - 1),
        match.index
      );
      if (!/^[\s\0]?$/.test(prefix)) {
        // eslint-disable-next-line
        continue;
      }

      const from = match.index + $position.start();
      const to = from + match[0].length;

      if (from < $position.pos && to >= $position.pos) {
        const fullMatch = match[0];
        const trimmedText = fullMatch ? fullMatch.slice(char.length) : '';
        return { range: { from, to }, text: trimmedText };
      }
    }
    return null;
  };
